import { useRef, useState } from "react";
import { App, Button, Flex, Input, Modal, Space, Typography } from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Trans, useTranslation } from "react-i18next";
import CopyIcon from "@/assets/copy.svg?react";
import WarningIcon from "@/assets/warning.svg?react";
import { ApiError } from "@/api/client";
import {
  getVerificationToken,
  resetVerificationToken,
  updateVerificationToken,
} from "@/api/events";
import { IconBtn } from "@/components/IconBtn";
import { brandModalBtnProps } from "@/components/BrandButton";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import { SectionCard } from "./SectionCard";
import EditIcon from "@/assets/edit.svg?react";
import type { EventPushConfig } from "./types";

function errorText(err: unknown, fallback: string) {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

const descLinkClass =
  "cursor-pointer border-0 bg-transparent p-0 text-accent underline";

export function EncryptionPanel({
  config,
  onChange,
  onGoConfig,
}: {
  config: EventPushConfig;
  onChange: (next: EventPushConfig) => void;
  onGoConfig: () => void;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const app = useCurrentApp();
  const configRef = useRef(config);
  configRef.current = config;
  // const [editingKey, setEditingKey] = useState(false);
  const [editingToken, setEditingToken] = useState(false);
  // const [draftKey, setDraftKey] = useState(config.encryptKey ?? "");
  const [draftToken, setDraftToken] = useState("");
  const [plainToken, setPlainToken] = useState<string | null>(null);
  const [tokenShown, setTokenShown] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetKind, setResetKind] = useState<"encrypt" | "token" | null>(null);

  const applyToken = (token: string) => {
    setPlainToken(token);
    setTokenShown(true);
    onChange({
      ...configRef.current,
      verificationToken: token,
    });
  };

  const loadPlainToken = async () => {
    if (plainToken !== null) return plainToken;
    const token = await getVerificationToken(app.id);
    setPlainToken(token);
    return token;
  };

  const toggleToken = async () => {
    if (revealing) return;
    if (tokenShown) {
      setTokenShown(false);
      return;
    }
    setRevealing(true);
    try {
      await loadPlainToken();
      setTokenShown(true);
    } catch (err) {
      message.error(errorText(err, t("events.loadFailed")));
    } finally {
      setRevealing(false);
    }
  };

  const copyToken = async () => {
    if (revealing) return;
    try {
      const token = await loadPlainToken();
      if (!token) return;
      await navigator.clipboard?.writeText(token);
      message.success(t("events.tokenCopied"));
    } catch (err) {
      message.error(errorText(err, t("events.loadFailed")));
    }
  };

  const openTokenEdit = async () => {
    if (revealing) return;
    setRevealing(true);
    try {
      const token = await loadPlainToken();
      setDraftToken(token);
      setEditingToken(true);
    } catch (err) {
      message.error(errorText(err, t("events.loadFailed")));
    } finally {
      setRevealing(false);
    }
  };

  const saveToken = async () => {
    const next = draftToken.trim();
    if (!next || savingToken) return;
    setSavingToken(true);
    try {
      const token = await updateVerificationToken(app.id, next);
      applyToken(token);
      setEditingToken(false);
      message.success(t("events.saved"));
    } catch (err) {
      message.error(errorText(err, t("events.saveFailed")));
    } finally {
      setSavingToken(false);
    }
  };

  const confirmReset = async () => {
    if (resetKind !== "token") {
      setResetKind(null);
      return;
    }
    if (resetting) return;
    setResetting(true);
    try {
      const token = await resetVerificationToken(app.id);
      applyToken(token);
      setEditingToken(false);
      setResetKind(null);
      message.success(t("events.tokenResetDone"));
    } catch (err) {
      message.error(errorText(err, t("events.resetFailed")));
    } finally {
      setResetting(false);
    }
  };

  return (
    <SectionCard>
      <Typography.Title level={5} className="!m-0">
        {t("events.subscriptionMethod")}
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="!my-4">
        <Trans
          i18nKey="events.encryptionDesc"
          components={{
            events: (
              <button
                type="button"
                className={descLinkClass}
                onClick={onGoConfig}
              />
            ),
            callbacks: (
              <button
                type="button"
                className={descLinkClass}
                onClick={onGoConfig}
              />
            ),
          }}
        />
      </Typography.Paragraph>

      {/* <Flex vertical gap={8}>
        <Typography.Text type="secondary">
          {t("events.encryptKey")}
        </Typography.Text>
        {editingKey ? (
          <Flex vertical gap="middle">
            <Input
              value={draftKey}
              onChange={(e) => setDraftKey(e.target.value)}
              className="min-w-[280px] font-mono"
            />
            <div className="flex gap-3">
              <Button
                type="primary"
                onClick={() => {
                  onChange({ ...config, encryptKey: draftKey.trim() || null });
                  setEditingKey(false);
                  message.success(t("events.saved"));
                }}
              >
                {t("common.save")}
              </Button>
              <Button
                onClick={() => {
                  setDraftKey(config.encryptKey ?? "");
                  setEditingKey(false);
                }}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </Flex>
        ) : (
          <Space>
            <Typography.Text className="font-mono">
              {config.encryptKey || t("events.encryptInactive")}
            </Typography.Text>
            <IconBtn
              label={t("common.reset")}
              onClick={() => setResetKind("encrypt")}
            >
              <SyncOutlined />
            </IconBtn>
            <IconBtn
              label={t("common.edit")}
              onClick={() => {
                setDraftKey(config.encryptKey ?? "");
                setEditingKey(true);
              }}
            >
              <EditIcon />
            </IconBtn>
          </Space>
        )}
      </Flex> */}

      <Flex vertical gap={8} className="!mt-4">
        <Typography.Text type="secondary">
          {t("events.verifyToken")}
        </Typography.Text>
        {editingToken ? (
          <Flex vertical gap="middle">
            <Input
              value={draftToken}
              onChange={(e) => setDraftToken(e.target.value)}
              className="min-w-[280px] font-mono"
            />
            <div className="flex gap-3">
              <Button
                type="primary"
                loading={savingToken}
                disabled={!draftToken.trim()}
                onClick={() => void saveToken()}
              >
                {t("common.save")}
              </Button>
              <Button
                onClick={() => {
                  setEditingToken(false);
                }}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </Flex>
        ) : (
          <Space>
            <Typography.Text className="font-mono tracking-wide">
              {tokenShown
                ? plainToken || t("common.empty")
                : config.verificationToken || t("common.empty")}
            </Typography.Text>
            <IconBtn
              label={tokenShown ? t("common.hide") : t("common.show")}
              onClick={() => void toggleToken()}
            >
              {tokenShown ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </IconBtn>
            <IconBtn label={t("common.copy")} onClick={() => void copyToken()}>
              <CopyIcon />
            </IconBtn>
            <IconBtn
              label={t("common.reset")}
              onClick={() => setResetKind("token")}
            >
              <SyncOutlined />
            </IconBtn>
            <IconBtn
              label={t("common.edit")}
              onClick={() => void openTokenEdit()}
            >
              <EditIcon />
            </IconBtn>
          </Space>
        )}
      </Flex>

      <Modal
        open={resetKind !== null}
        centered
        width={480}
        title={
          <Space size={20} align="center">
            <WarningIcon className="size-7 text-[#F77234]" />
            <span>
              {resetKind === "encrypt"
                ? t("events.resetEncryptTitle")
                : t("events.resetTokenTitle")}
            </span>
          </Space>
        }
        okText={t("events.confirmReset")}
        cancelText={t("common.cancel")}
        confirmLoading={resetting}
        {...brandModalBtnProps("danger")}
        onOk={() => void confirmReset()}
        onCancel={() => {
          if (!resetting) setResetKind(null);
        }}
        styles={{
          body: {
            paddingLeft: "48px",
          },
        }}
      >
        <Typography.Paragraph type="secondary" className="!mb-0">
          {resetKind === "encrypt"
            ? t("events.resetEncryptConfirm")
            : t("events.resetTokenConfirm")}
        </Typography.Paragraph>
      </Modal>
    </SectionCard>
  );
}
